import { EventEmitter } from '../../shared/EventEmitter';
export const LOAD_EDITOR = 'LOAD_EDITOR';
/**
 * Component that build monaco editor element
 *
 * @param {string} width - Container width
 * @param {string} height - Container height
 * @param {string} language - Editor language
 * @param {string} theme - Editor theme (vs, vs-dark or hc-black)
 * @param {any} code - Editor content code
 * @param {function} onDidCreateEditor - Event dispatched after editor created
 */
class MonacoEditor {
    static $inject = ['$scope', '$timeout', '$element'];
    constructor($scope, $timeout, $element) {
        'ngInject';
        this.$scope = $scope;
        this.$timeout = $timeout;
        this.$element = $element;
        this.hasEditor = false;
        this.errorHandler = null;
        this.rejectionHandler = null;
        this.handlersAttached = false;
        this.creatingEditor = false;
        this.saveKeyListener = null;
    }
    get containerWidth() {
        return this.width || '500px';
    }
    get containerHeight() {
        return this.height || '500px';
    }
    $onChanges(changes) {
        if (changes.code && changes.code.currentValue) {
            this.$timeout(() => {
                this.editor && this.editor.setValue(changes.code.currentValue);
            });
        }
    }
    async initEditor() {
        // Prevent concurrent initializations
        if (this.creatingEditor) {
            return;
        }
        this.creatingEditor = true;
        try {
            // If an editor already exists, just relayout and update content
            if (this.editor) {
                try {
                    this.editor.layout();
                    if (this.code != null) {
                        this.editor.setValue(this.code);
                    }
                    return;
                }
                finally {
                    this.creatingEditor = false;
                }
            }
            // Clear container to remove any leftover Monaco DOM nodes
            const container = this.$element[0].querySelector('#monaco-editor-container');
            if (container) {
                container.innerHTML = '';
            }
            const monaco = await import('monaco-editor');
            // Catch and suppress worker-related errors globally
            // Monaco workers throw "Unexpected usage" errors that don't affect basic editor functionality
            const originalError = console.error;
            let suppressedCount = 0;
            this.errorHandler = (event) => {
                if (event.message && event.message.includes('Unexpected usage')) {
                    suppressedCount++;
                    if (suppressedCount === 1) {
                        console.warn('Monaco editor: Worker limitation detected. Basic code editing will work normally.');
                    }
                    event.preventDefault();
                    return false;
                }
            };
            if (!this.handlersAttached) {
                window.addEventListener('error', this.errorHandler);
                this.handlersAttached = true;
            }
            console.error = (message, ...args) => {
                if (typeof message === 'string' && message.includes('Unexpected usage')) {
                    return; // Suppress from console too
                }
                originalError.call(console, message, ...args);
            };
            if (this.onDidCreateEditor) {
                this.handleOnDidCreateEditor = monaco.editor.onDidCreateEditor((editor) => {
                    this.onDidCreateEditor(EventEmitter({ editor }));
                });
            }
            this.editor = monaco.editor.create(this.$element[0].querySelector('#monaco-editor-container'), {
                value: this.code,
                language: this.language,
                theme: this.theme || 'vs-dark',
                fontLigatures: true,
            });
            this.hasEditor = true;
            // Fallback keybinding guard: Ensure Ctrl+S triggers onSave even if Monaco keybinding fails or focus edge cases occur
            const domNode = this.editor.getDomNode();
            if (domNode && !this.saveKeyListener) {
                this.saveKeyListener = (evt) => {
                    if ((evt.ctrlKey || evt.metaKey) && (evt.key === 's' || evt.key === 'S')) {
                        // If Monaco didn't handle it, prevent default browser Save Page dialog
                        evt.preventDefault();
                        evt.stopPropagation();
                        if (this.onSave) {
                            try {
                                this.onSave(EventEmitter({ editor: this.editor }));
                            }
                            catch (err) {
                                console.error('Error executing onSave handler:', err);
                            }
                        }
                    }
                };
                domNode.addEventListener('keydown', this.saveKeyListener, { capture: true });
            }
            // Restore original console.error (keep error event listener active)
            console.error = originalError;
            // Also suppress unhandled promise rejections from Monaco workers
            if (!this.rejectionHandler) {
                this.rejectionHandler = (event) => {
                    if (event.reason && typeof event.reason === 'object') {
                        const reason = event.reason;
                        if (reason.message && reason.message.includes('Unexpected usage')) {
                            event.preventDefault();
                            return false;
                        }
                    }
                };
                window.addEventListener('unhandledrejection', this.rejectionHandler);
            }
            this.addEditorActions(monaco);
            if (this.onCodeChange) {
                this.handleCodeChanges = this.editor.onDidChangeModelContent((event) => {
                    this.onCodeChange(EventEmitter({
                        code: this.editor.getValue(),
                        event,
                    }));
                });
            }
        }
        catch (error) {
            console.error(error);
        }
        finally {
            this.creatingEditor = false;
        }
    }
    async $onInit() {
        if (!this.lazyLoad) {
            await this.initEditor();
        }
        else {
            this.$scope.$on(LOAD_EDITOR, async () => {
                if (!this.hasEditor) {
                    this.$timeout(async () => {
                        await this.initEditor();
                        this.hasEditor = true;
                    });
                }
            });
        }
    }
    addEditorActions(monaco) {
        // Ctrl+S action dispatch onSave event
        this.editor.addAction({
            id: 'save-action',
            label: 'Save',
            keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KEY_S],
            precondition: undefined,
            keybindingContext: undefined,
            run: (editor) => {
                if (this.onSave) {
                    this.onSave(EventEmitter({ editor }));
                }
            },
        });
    }
    $onDestroy() {
        this.handleOnDidCreateEditor && this.handleOnDidCreateEditor.dispose();
        this.handleCodeChanges && this.handleCodeChanges.dispose();
        this.editor && this.editor.dispose();
        // Clean up event listeners
        if (this.errorHandler) {
            window.removeEventListener('error', this.errorHandler);
        }
        if (this.rejectionHandler) {
            window.removeEventListener('unhandledrejection', this.rejectionHandler);
        }
        this.handlersAttached = false;
        // Clear container
        const container = this.$element[0].querySelector('#monaco-editor-container');
        if (container) {
            container.innerHTML = '';
        }
        // Remove save key listener if attached
        if (this.saveKeyListener && this.editor) {
            const domNode = this.editor.getDomNode();
            domNode && domNode.removeEventListener('keydown', this.saveKeyListener, { capture: true });
        }
        this.saveKeyListener = null;
    }
}
MonacoEditor.$inject = ['$scope', '$timeout', '$element'];
export const MonacoEditorComponent = {
    template: `
        <div id="monaco-editor-container"
            ng-style="{ width: $ctrl.containerWidth, height: $ctrl.containerHeight }"
            ></div>
    `,
    controller: MonacoEditor,
    controllerAs: '$ctrl',
    bindings: {
        width: '@?',
        height: '@?',
        language: '@?',
        theme: '@?',
        code: '@?',
        lazyLoad: '<?',
        onDidCreateEditor: '&?',
        onCodeChange: '&?',
        onSave: '&?',
    },
    transclude: false,
};
